import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import duckIcon from '../assets/defaultDuckNewIcon.png';
import tokenIcon from '../assets/duck-token.png';
import { clearUserProfileCache } from '../services/userProfileCache';
import { subscribeRequests } from '../services/friends';
import { subscribeMyParticipantsForUid } from '../services/messages';
import { subscribeNotifications } from '../services/notifications';
import { usePremiumStatus } from '../hooks/usePremiumStatus';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../contexts/SearchContext';
import { FaBars, FaBell, FaSearch, FaTimes } from 'react-icons/fa';
import '../styles/Header.css';
import '../styles/HeaderSearch.css';


function Header({ user, mobileOpen, setMobileOpen }) {
  const navigate = useNavigate();
  const { userDoc, isSuspended: authSuspended } = useAuth();
  const mobileMenuRef = useRef(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsMenuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const { isPremium, isAdmin } = usePremiumStatus(user);
  const accountLabel = isPremium ? 'Premium Member' : 'Account';
  const menuSubtitle = isAdmin ? `${accountLabel} · Admin` : accountLabel;
  const { searchQuery, setSearchQuery, isSearchOpen, setIsSearchOpen, clearSearch } = useSearch();
  const searchInputRef = useRef(null);
  const tokens = Number(userDoc?.tokens || 0);
  const isSuspended = Boolean(authSuspended);

  const totalBadgeCount = unreadMessageCount + unreadNotificationCount + pendingCount;

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    hamburgerRef.current?.focus();
  }, [setMobileOpen]);

  useEffect(() => {
    if (!user) {
      setPendingCount(0);
      return undefined;
    }
    const unsub = subscribeRequests(
      user.uid,
      (requests) => setPendingCount(Array.isArray(requests) ? requests.length : 0),
      (err) => console.error('Header friendRequests snapshot error', err)
    );
    return () => {
      try { unsub(); } catch (err) { console.error('Header friendRequests cleanup failed', err); }
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadMessageCount(0);
      return undefined;
    }
    const unsub = subscribeMyParticipantsForUid(user.uid, (participants) => {
      const list = Object.values(participants || {});
      const total = list.reduce((sum, entry) => sum + Math.max(0, Number(entry?.unreadCount || 0)), 0);
      setUnreadMessageCount(total);
    });
    return () => {
      try { unsub(); } catch (err) { console.error('Header participants cleanup failed', err); }
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadNotificationCount(0);
      return undefined;
    }
    const unsub = subscribeNotifications(user.uid, (items) => {
      const total = (items || []).reduce((sum, item) => sum + (item?.isRead ? 0 : 1), 0);
      setUnreadNotificationCount(total);
    });
    return () => {
      try { unsub(); } catch (err) { console.error('Header notifications cleanup failed', err); }
    };
  }, [user]);

  useEffect(() => {
    const handleOutside = (event) => {
      const target = event.target;
      if (settingsOpen && settingsMenuRef.current && !settingsMenuRef.current.contains(target)) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [settingsOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const menu = document.getElementById('mobile-menu');
    if (!menu) return;
    const focusable = menu.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeMobile();
    };

    first.focus();
    document.addEventListener('keydown', handleTab);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleTab);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [mobileOpen, closeMobile]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (mobileOpen) {
      root.classList.add('mobile-menu-open');
      body.classList.add('mobile-menu-open');
    } else {
      root.classList.remove('mobile-menu-open');
      body.classList.remove('mobile-menu-open');
    }

    return () => {
      root.classList.remove('mobile-menu-open');
      body.classList.remove('mobile-menu-open');
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      clearUserProfileCache();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="header">
      <div className="header-container">
        <button
          ref={hamburgerRef}
          className="mobile-menu-toggle"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-controls="mobile-menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((o) => !o)}
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
          {!mobileOpen && totalBadgeCount > 0 && (
            <span className="hamburger-badge" aria-hidden="true">{totalBadgeCount}</span>
          )}
        </button>
        <div className="header-brand-row">
          <Link to="/" className="logo">
            <img src={duckIcon} alt="Duckerr logo" className="logo-icon" />
            <span className="logo-text">Duckerr</span>
          </Link>
          {user ? (
            <Link to="/dashboard" className="header-dashboard-shortcut">
              Dashboard
            </Link>
          ) : null}
        </div>
        {user ? (
          <>
            <nav className="nav-links">
              <div className="nav-primary">
                <NavLink to="/view-ducks" className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }>Duck Pond</NavLink>
                <NavLink to="/private-ducks" className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }>My Ducks</NavLink>
                <NavLink to="/messages" className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }>
                  Inbox
                  {unreadMessageCount > 0 && (
                    <span className="nav-badge">{unreadMessageCount}</span>
                  )}
                </NavLink>
                <NavLink to="/notifications" className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                } aria-label="Notifications" title="Notifications">
                  <span className="nav-icon" aria-hidden="true"><FaBell /></span>
                  {unreadNotificationCount > 0 && (
                    <span className="nav-badge">{unreadNotificationCount}</span>
                  )}
                </NavLink>
                {!isSuspended && (
                  <NavLink
                    to="/create-duck"
                    className={({ isActive }) =>
                      isActive ? "nav-link nav-cta active" : "nav-link nav-cta"
                    }
                  >
                    Hatch Duck
                  </NavLink>
                )}
              </div>
              <div className="header-search">
                {isSearchOpen ? (
                  <div className="header-search-input-wrap">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search this page…"
                      aria-label="Search current page"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Escape') clearSearch(); }}
                    />
                    <button
                      className="header-search-clear"
                      onClick={clearSearch}
                      aria-label="Close search"
                      type="button"
                    >
                      <FaTimes aria-hidden="true" />
                    </button>
                  </div>
                ) : (
                  <button
                    className="header-search-btn"
                    onClick={() => setIsSearchOpen(true)}
                    aria-label="Open search"
                    type="button"
                    title="Search current page"
                  >
                    <FaSearch aria-hidden="true" />
                  </button>
                )}
              </div>
            </nav>

            <div className="user-actions">
              <div className="user-actions-row">
                <div ref={settingsMenuRef} className="settings-menu-trigger">
                  <button
                    onClick={() => setSettingsOpen((o) => !o)}
                    className="user-info-inline"
                    aria-haspopup="menu"
                    aria-expanded={settingsOpen}
                    title="Account menu"
                    type="button"
                  >
                    <div className="user-avatar">
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt={user.displayName || 'You'}
                          className="avatar-image"
                        />
                      ) : (
                        <div className="avatar-placeholder">
                          {(user.displayName || user.email || 'You').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="user-name-display">{user.displayName || 'You'}</span>
                  </button>
                  {settingsOpen && (
                    <div role="menu" className="header-menu">
                      <div className="header-menu-header">
                        <div className="header-menu-name">{user.displayName || 'You'}</div>
                        <div className="header-menu-subtitle">{menuSubtitle}</div>
                        <div className="header-menu-tokens">
                          <img src={tokenIcon} alt="" className="token-badge-icon" />
                          <span>{tokens} Tokens</span>
                        </div>
                      </div>
                      <Link to="/dashboard" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Dashboard
                      </Link>
                      <Link to={`/user/${user.uid}`} role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        My Profile
                      </Link>
                      <Link to="/friends" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Friends{pendingCount > 0 ? ` (${pendingCount})` : ''}
                      </Link>
                      <Link to="/following" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Following
                      </Link>
                      <Link to="/tag-orders" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        My Tag Orders
                      </Link>
                      <div className="header-menu-divider" />
                      <Link to={isPremium ? "/garage" : "/shop?upgrade=premium"} role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Garage{isPremium ? '' : ' (Premium)'}
                      </Link>
                      <Link to={isPremium ? "/swap-meet" : "/shop?upgrade=premium"} role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Swap Meet{isPremium ? '' : ' (Premium)'}
                      </Link>
                      <Link to="/feed" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Feed
                      </Link>
                      <Link to="/leaderboards" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Leaderboards
                      </Link>
                      <Link to="/trophy-room" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Trophy Room
                      </Link>
                      <div className="header-menu-divider" />
                      <Link to="/shop" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Token Shop
                      </Link>
                      {!isPremium && (
                        <Link to="/shop?upgrade=premium" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item header-menu-premium">
                          Go Premium
                        </Link>
                      )}
                      <Link to="/settings" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                        Settings
                      </Link>
                      {isAdmin && (
                        <>
                          <div className="header-menu-divider" />
                          <div className="header-menu-section">Admin</div>
                          <Link to="/admin" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                            Admin Panel
                          </Link>
                          <Link to="/admin/tag-orders" role="menuitem" onClick={() => setSettingsOpen(false)} className="header-menu-item">
                            Tag Fulfillment
                          </Link>
                        </>
                      )}
                      <div className="header-menu-divider" />
                      <button
                        role="menuitem"
                        onClick={() => { setSettingsOpen(false); handleLogout(); }}
                        className="header-menu-item header-menu-logout"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={`mobile-menu-overlay ${mobileOpen ? 'open' : ''}`} onClick={closeMobile} aria-hidden={!mobileOpen} />
            <nav id="mobile-menu" className={`mobile-menu ${mobileOpen ? 'open' : ''}`} aria-label="Mobile Navigation">
              <div className="mobile-menu-content" ref={mobileMenuRef} tabIndex={-1}>
                <div className="mobile-menu-profile">
                  <div className="user-avatar mobile-avatar">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName || 'You'} className="avatar-image" />
                    ) : (
                      <div className="avatar-placeholder">
                        {(user.displayName || user.email || 'You').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="mobile-menu-profile-info">
                    <div className="mobile-menu-profile-name">{user.displayName || 'You'}</div>
                    <div className="mobile-menu-profile-tokens">
                      <img src={tokenIcon} alt="" className="token-badge-icon" />
                      <span>{tokens} Tokens</span>
                    </div>
                  </div>
                </div>

                <div className="mobile-section-label">Explore</div>
                <NavLink to="/view-ducks" onClick={closeMobile} className="mobile-nav-link">Duck Pond</NavLink>
                <NavLink to="/private-ducks" onClick={closeMobile} className="mobile-nav-link">My Ducks</NavLink>
                {!isSuspended && (
                  <NavLink to="/create-duck" onClick={closeMobile} className="mobile-nav-link mobile-nav-cta">Hatch Duck</NavLink>
                )}
                <NavLink to="/messages" onClick={closeMobile} className="mobile-nav-link">
                  Inbox
                  {unreadMessageCount > 0 && <span className="nav-badge">{unreadMessageCount}</span>}
                </NavLink>
                <NavLink to="/notifications" onClick={closeMobile} className="mobile-nav-link">
                  Notifications
                  {unreadNotificationCount > 0 && <span className="nav-badge">{unreadNotificationCount}</span>}
                </NavLink>
                <NavLink to="/shop" onClick={closeMobile} className="mobile-nav-link">Token Shop</NavLink>

                <div className="mobile-section-label">Community</div>
                <NavLink to="/friends" onClick={closeMobile} className="mobile-nav-link">
                  Friends{pendingCount > 0 ? ` (${pendingCount})` : ''}
                </NavLink>
                <NavLink to="/feed" onClick={closeMobile} className="mobile-nav-link">
                  Feed
                </NavLink>
                <NavLink to={isPremium ? "/garage" : "/shop?upgrade=premium"} onClick={closeMobile} className="mobile-nav-link">
                  Garage{isPremium ? '' : ' (Premium)'}
                </NavLink>
                <NavLink to={isPremium ? "/swap-meet" : "/shop?upgrade=premium"} onClick={closeMobile} className="mobile-nav-link">
                  Swap Meet{isPremium ? '' : ' (Premium)'}
                </NavLink>
                <NavLink to="/leaderboards" onClick={closeMobile} className="mobile-nav-link">Leaderboards</NavLink>

                <div className="mobile-section-label">Account</div>
                <NavLink to="/dashboard" onClick={closeMobile} className="mobile-nav-link">Dashboard</NavLink>
                <NavLink to={`/user/${user?.uid || ''}`} onClick={closeMobile} className="mobile-nav-link">My Profile</NavLink>
                <NavLink to="/tag-orders" onClick={closeMobile} className="mobile-nav-link">My Tag Orders</NavLink>
                {!isPremium && (
                  <NavLink to="/shop?upgrade=premium" onClick={closeMobile} className="mobile-nav-link mobile-nav-premium">Go Premium</NavLink>
                )}
                <NavLink to="/settings" onClick={closeMobile} className="mobile-nav-link">Settings</NavLink>
                {isAdmin && (
                  <>
                    <div className="mobile-section-label">Admin</div>
                    <NavLink to="/admin" onClick={closeMobile} className="mobile-nav-link">Admin Panel</NavLink>
                    <NavLink to="/admin/tag-orders" onClick={closeMobile} className="mobile-nav-link">Tag Fulfillment</NavLink>
                  </>
                )}
                <div className="mobile-divider" />
                <button onClick={() => { closeMobile(); handleLogout(); }} className="mobile-action">Logout</button>
              </div>
            </nav>
          </>
        ) : (
          <>
            <div className="auth-buttons">
              <Link to="/login" className="btn btn-outline btn-sm">Log In</Link>
              <Link to="/signup" className="btn btn-sm">Sign Up</Link>
            </div>
            <div className={`mobile-menu-overlay ${mobileOpen ? 'open' : ''}`} onClick={closeMobile} aria-hidden={!mobileOpen} />
            <nav id="mobile-menu" className={`mobile-menu mobile-menu--guest ${mobileOpen ? 'open' : ''}`} aria-label="Mobile Navigation">
              <div className="mobile-menu-content" ref={mobileMenuRef} tabIndex={-1}>
                <div className="mobile-menu-profile mobile-menu-profile--guest">
                  <img src={duckIcon} alt="" className="mobile-guest-duck" />
                  <div className="mobile-menu-profile-info">
                    <div className="mobile-menu-profile-name">Welcome to Duckerr</div>
                    <div className="mobile-menu-profile-subtitle">Sign up to hatch, catch, and track ducks.</div>
                  </div>
                </div>

                <div className="mobile-guest-actions">
                  <NavLink to="/signup" onClick={closeMobile} className="mobile-guest-primary">Sign Up</NavLink>
                  <NavLink to="/login" onClick={closeMobile} className="mobile-guest-secondary">Log In</NavLink>
                </div>

                <div className="mobile-section-label">Explore</div>
                <NavLink to="/" onClick={closeMobile} className="mobile-nav-link">Home</NavLink>
                <NavLink to="/view-ducks" onClick={closeMobile} className="mobile-nav-link">Duck Pond</NavLink>
                <NavLink to="/got-ducked" onClick={closeMobile} className="mobile-nav-link">Got Ducked?</NavLink>
                <NavLink to="/leaderboards" onClick={closeMobile} className="mobile-nav-link">Leaderboards</NavLink>
                <NavLink to="/faq" onClick={closeMobile} className="mobile-nav-link">FAQ</NavLink>
                <div className="mobile-divider" />
                <NavLink to="/privacy" onClick={closeMobile} className="mobile-nav-link">Privacy</NavLink>
                <NavLink to="/terms" onClick={closeMobile} className="mobile-nav-link">Terms</NavLink>
              </div>
            </nav>
          </>
        )}
      </div>
    </header>
  );
}

export default Header; 